import {
  CANVAS_SELECTION_ROLE,
  type CanvasTemplateSemanticSpec,
  normalizeCanvasSemanticText,
} from '@streamlain/canvas-selection-contract';

import type { CanvasSelectionItem } from '../components/ai-chat-content/type';

type Bounds = NonNullable<CanvasSelectionItem['bounds']>;

interface OrganizeCanvasSelectionOptions {
  templateSpec?: CanvasTemplateSemanticSpec | null;
}

type AreaSection = {
  container: CanvasSelectionItem;
  label?: CanvasSelectionItem;
  labelText: string;
  targets: CanvasSelectionItem[];
};

export function flattenCanvasSelectionItems(
  items: CanvasSelectionItem[]
): CanvasSelectionItem[] {
  const out: CanvasSelectionItem[] = [];
  for (const item of items) {
    out.push(item);
    if (item.children?.length) {
      out.push(...flattenCanvasSelectionItems(item.children));
    }
  }
  return out;
}

function getAreaLabel(
  item: CanvasSelectionItem,
  spec: CanvasTemplateSemanticSpec
): string | null {
  const text = normalizeCanvasSemanticText(item.text ?? item.label);
  for (const area of spec.areas) {
    if (area.aliases.some(alias => text === alias)) {
      return area.label;
    }
  }
  return null;
}

function area(bounds: Bounds): number {
  return bounds.w * bounds.h;
}

function center(bounds: Bounds): { x: number; y: number } {
  return {
    x: bounds.x + bounds.w / 2,
    y: bounds.y + bounds.h / 2,
  };
}

function containsPoint(bounds: Bounds, x: number, y: number): boolean {
  return (
    x >= bounds.x &&
    x <= bounds.x + bounds.w &&
    y >= bounds.y &&
    y <= bounds.y + bounds.h
  );
}

function sortTopLeft(a: CanvasSelectionItem, b: CanvasSelectionItem): number {
  const aBounds = a.bounds;
  const bBounds = b.bounds;
  if (!aBounds || !bBounds) return a.id.localeCompare(b.id);
  return aBounds.y - bBounds.y || aBounds.x - bBounds.x;
}

function isPotentialAreaContainer(item: CanvasSelectionItem): boolean {
  if (!item.bounds) return false;
  const normalizedType = normalizeCanvasSemanticText(item.type);
  return (
    item.role === CANVAS_SELECTION_ROLE.Frame ||
    item.role === CANVAS_SELECTION_ROLE.Group ||
    item.type === 'shape' ||
    normalizedType.includes('shape') ||
    normalizedType.includes('frame') ||
    normalizedType.includes('group')
  );
}

function matchesAliases(value: string, aliases: readonly string[]): boolean {
  const normalizedValue = normalizeCanvasSemanticText(value);
  return aliases.some(
    alias => normalizedValue === normalizeCanvasSemanticText(alias)
  );
}

function isEditableTemplateTarget(
  item: CanvasSelectionItem,
  spec: CanvasTemplateSemanticSpec
): boolean {
  if (item.kind === 'block' && item.type === 'affine:note') return true;
  const aliases = spec.editableTargetAliases ?? [];
  if (aliases.length === 0) return false;
  return [item.text, item.label].some(
    value => typeof value === 'string' && matchesAliases(value, aliases)
  );
}

function isInsideContainer(
  item: CanvasSelectionItem,
  container: CanvasSelectionItem
): boolean {
  if (item.groupId === container.id) return true;
  if (!item.bounds || !container.bounds) return false;
  const itemCenter = center(item.bounds);
  return containsPoint(container.bounds, itemCenter.x, itemCenter.y);
}

function findContainerForLabel(
  items: CanvasSelectionItem[],
  label: CanvasSelectionItem
): CanvasSelectionItem | null {
  if (!label.bounds) return null;
  const labelBounds = label.bounds;
  const labelCenter = center(label.bounds);
  return (
    items
      .filter(candidate => candidate.id !== label.id)
      .filter(isPotentialAreaContainer)
      .filter(candidate => {
        if (!candidate.bounds) return false;
        if (!containsPoint(candidate.bounds, labelCenter.x, labelCenter.y)) {
          return false;
        }
        return area(candidate.bounds) > area(labelBounds) * 4;
      })
      .sort((a, b) => area(a.bounds!) - area(b.bounds!))[0] ?? null
  );
}

function findAreaSections(
  items: CanvasSelectionItem[],
  spec: CanvasTemplateSemanticSpec
): AreaSection[] {
  const sections: AreaSection[] = [];
  const usedContainerIds = new Set<string>();

  for (const container of items) {
    const labelText = getAreaLabel(container, spec);
    if (
      !labelText ||
      !isPotentialAreaContainer(container) ||
      usedContainerIds.has(container.id)
    ) {
      continue;
    }

    const targets = items
      .filter(item => item.id !== container.id)
      .filter(item => isEditableTemplateTarget(item, spec))
      .filter(item => isInsideContainer(item, container))
      .sort(sortTopLeft);

    if (!targets.length) continue;
    usedContainerIds.add(container.id);
    sections.push({ container, labelText, targets });
  }

  for (const label of items) {
    const labelText = getAreaLabel(label, spec);
    if (!labelText || !label.bounds) continue;
    if (usedContainerIds.has(label.id)) continue;

    const container = findContainerForLabel(items, label);
    if (!container?.bounds || usedContainerIds.has(container.id)) continue;

    const targets = items
      .filter(item => item.id !== label.id && item.id !== container.id)
      .filter(item => isEditableTemplateTarget(item, spec))
      .filter(item => isInsideContainer(item, container))
      .sort(sortTopLeft);

    if (!targets.length) continue;
    usedContainerIds.add(container.id);
    sections.push({ container, label, labelText, targets });
  }

  return sections.sort((a, b) => sortTopLeft(a.container, b.container));
}

function normalizeTree(
  items: CanvasSelectionItem[],
  parentId: string | null = null,
  depth = 0
): CanvasSelectionItem[] {
  return items.map(item => {
    const children = item.children?.length
      ? normalizeTree(item.children, item.id, depth + 1)
      : undefined;
    return {
      ...item,
      parentId,
      depth,
      childCount: children?.length ?? 0,
      children: children?.length ? children : undefined,
    };
  });
}

export function organizeCanvasSelectionItems(
  items: CanvasSelectionItem[],
  options: OrganizeCanvasSelectionOptions = {}
): CanvasSelectionItem[] {
  const spec = options.templateSpec;
  if (!spec) return normalizeTree(items);

  const sections = findAreaSections(items, spec);
  if (!sections.length) return normalizeTree(items);

  const areaContainerIds = new Set(
    sections.map(section => section.container.id)
  );
  const areaChildIds = new Set<string>();
  const areaItems = sections.map(section => {
    const label: CanvasSelectionItem | null = section.label
      ? {
          ...section.label,
          label: section.labelText,
          text: section.label.text ?? section.labelText,
          role: CANVAS_SELECTION_ROLE.AreaLabel,
        }
      : null;
    if (label) areaChildIds.add(label.id);

    const targets = section.targets.map(target => {
      areaChildIds.add(target.id);
      return {
        ...target,
        role: CANVAS_SELECTION_ROLE.EditableTarget,
      };
    });

    return {
      ...section.container,
      label: section.labelText,
      text: section.labelText,
      role: CANVAS_SELECTION_ROLE.Area,
      children: label ? [label, ...targets] : targets,
    };
  });

  const helperItems = items
    .filter(
      item => !areaContainerIds.has(item.id) && !areaChildIds.has(item.id)
    )
    .map(item => ({
      ...item,
      role:
        item.role === CANVAS_SELECTION_ROLE.Area
          ? item.role
          : CANVAS_SELECTION_ROLE.TemplateHelper,
    }));

  return normalizeTree([...areaItems, ...helperItems]);
}
